// client/src/components/secure/training/shared.js — small pieces the training screens share.
import React from 'react';

export const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start p-4 overflow-y-auto">
        <div className={`bg-white p-6 md:p-8 rounded-lg shadow-xl w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} my-8`}>
            <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-slate-800">{title}</h2>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none" aria-label="Close">&times;</button>
            </div>
            {children}
        </div>
    </div>
);

// A plain date ('2026-10-03') is a calendar day; a timestamp ('…T…Z') is shown in local time.
export const fmtDate = (d) => {
    if (!d) return '—';
    const v = String(d);
    return (v.length > 10 && v.includes('T') ? new Date(v) : new Date(`${v.slice(0, 10)}T12:00:00`)).toLocaleDateString();
};
export const fmtDateTime = (d) => (d ? new Date(String(d).replace(' ', 'T')).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const ROLE_LABEL = { all: 'Everyone', agent: 'Agents', managing_broker: 'Managing broker', office_staff: 'Office staff', pm_staff: 'PM staff', supervisor: 'Supervisors' };
export const FREQ_LABEL = { once: 'Once', annual: 'Every year', per_licence_cycle: 'Each licence cycle' };

const STATUS_STYLE = {
    draft: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', retired: 'bg-slate-200 text-slate-600',
    assigned: 'bg-blue-100 text-blue-800', in_progress: 'bg-indigo-100 text-indigo-800', completed: 'bg-emerald-100 text-emerald-800',
    waived: 'bg-slate-200 text-slate-600', overdue: 'bg-red-100 text-red-800',
    complete: 'bg-emerald-100 text-emerald-800', ok: 'bg-slate-100 text-slate-700', warn: 'bg-amber-100 text-amber-800',
    urgent: 'bg-orange-100 text-orange-800', no_rule: 'bg-slate-200 text-slate-600',
};
const STATUS_TEXT = { in_progress: 'in progress', no_rule: 'no CE rule', ok: 'on track', warn: 'deadline approaching', urgent: '30 days or less', overdue: 'overdue' };
export const Pill = ({ s, children }) => (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[s] || 'bg-slate-100 text-slate-700'}`}>{children || STATUS_TEXT[s] || s}</span>
);

export const Notice = ({ tone = 'info', children }) => {
    const t = { info: 'bg-blue-50 border-blue-300 text-blue-900', warn: 'bg-amber-50 border-amber-300 text-amber-900', bad: 'bg-red-50 border-red-300 text-red-900', ok: 'bg-emerald-50 border-emerald-300 text-emerald-900' }[tone];
    return <div className={`border-l-4 rounded p-3 text-sm ${t}`}>{children}</div>;
};

export const Btn = ({ kind = 'primary', className = '', ...p }) => {
    const k = { primary: 'bg-slate-800 text-white hover:bg-slate-900', ghost: 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50',
        danger: 'bg-red-600 text-white hover:bg-red-700', ok: 'bg-emerald-600 text-white hover:bg-emerald-700' }[kind];
    return <button type="button" {...p} className={`px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 ${k} ${className}`} />;
};

export const Field = ({ label, children, hint }) => (
    <label className="block text-sm mb-3">
        <span className="block font-medium text-slate-700 mb-1">{label}</span>
        {children}
        {hint ? <span className="block text-xs text-slate-500 mt-1">{hint}</span> : null}
    </label>
);
export const inputCls = 'w-full border border-slate-300 rounded px-2 py-1.5 text-sm';

// ---------------------------------------------------------------------------
// A small, safe Markdown renderer (React elements only, never innerHTML):
// headings, paragraphs, bullet and numbered lists, block quotes, tables,
// **bold**, *italic*, `code` and [links](https://…).
// ---------------------------------------------------------------------------
function inline(text, keyBase) {
    const out = [];
    const re = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^)\s]+)\))/g;
    let last = 0;
    let m;
    let i = 0;
    while ((m = re.exec(text))) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const t = m[0];
        const k = `${keyBase}-${i += 1}`;
        if (t.startsWith('**')) out.push(<strong key={k}>{t.slice(2, -2)}</strong>);
        else if (t.startsWith('`')) out.push(<code key={k} className="bg-slate-100 px-1 rounded">{t.slice(1, -1)}</code>);
        else if (t.startsWith('[')) {
            const label = t.slice(1, t.indexOf(']'));
            out.push(<a key={k} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline">{label}</a>);
        } else out.push(<em key={k}>{t.slice(1, -1)}</em>);
        last = m.index + t.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

export const Markdown = ({ text }) => {
    const lines = String(text || '').replace(/\r/g, '').split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) { i += 1; continue; }
        const b = `b${blocks.length}`;
        const h = /^(#{1,4})\s+(.*)$/.exec(line);
        if (h) {
            const cls = ['text-xl font-bold', 'text-lg font-semibold', 'text-base font-semibold', 'text-sm font-semibold'][h[1].length - 1];
            blocks.push(<p key={b} className={`${cls} mt-4 mb-2 text-slate-800`}>{inline(h[2], b)}</p>);
            i += 1; continue;
        }
        if (/^\s*\|/.test(line)) {
            const rows = [];
            while (i < lines.length && /^\s*\|/.test(lines[i])) { rows.push(lines[i]); i += 1; }
            const cells = (r) => r.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
            const body = rows.filter((r) => !/^\s*\|[\s:|-]+\|\s*$/.test(r)).map(cells);
            const [head, ...rest] = body;
            blocks.push(
                <div key={b} className="overflow-x-auto my-3">
                    <table className="min-w-full text-sm border border-slate-200">
                        <thead className="bg-slate-50"><tr>{(head || []).map((c, j) => <th key={j} className="px-2 py-1 text-left border-b">{inline(c, `${b}h${j}`)}</th>)}</tr></thead>
                        <tbody>{rest.map((r, ri) => <tr key={ri} className="border-b">{r.map((c, j) => <td key={j} className="px-2 py-1 align-top">{inline(c, `${b}r${ri}c${j}`)}</td>)}</tr>)}</tbody>
                    </table>
                </div>);
            continue;
        }
        if (/^\s*>/.test(line)) {
            const q = [];
            while (i < lines.length && /^\s*>/.test(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, '')); i += 1; }
            blocks.push(<blockquote key={b} className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 my-3 text-sm">{inline(q.join(' '), b)}</blockquote>);
            continue;
        }
        if (/^\s*([*-]|\d+\.)\s+/.test(line)) {
            const ordered = /^\s*\d+\./.test(line);
            const items = [];
            while (i < lines.length && /^\s*([*-]|\d+\.)\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*([*-]|\d+\.)\s+/, '')); i += 1; }
            const Tag = ordered ? 'ol' : 'ul';
            blocks.push(<Tag key={b} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-6 my-2 space-y-1`}>{items.map((it, j) => <li key={j}>{inline(it, `${b}l${j}`)}</li>)}</Tag>);
            continue;
        }
        const para = [];
        while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*\||\s*>|\s*([*-]|\d+\.)\s)/.test(lines[i])) { para.push(lines[i]); i += 1; }
        blocks.push(<p key={b} className="my-2 leading-relaxed">{inline(para.join(' '), b)}</p>);
    }
    return <div className="text-slate-800">{blocks}</div>;
};

export const openBlob = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10000);
};

/** GET a file with the bearer token (the API client only does blobs on POST). */
export const fetchBlob = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
        let msg = `Download failed (${res.status})`;
        try { msg = (await res.json()).message || msg; } catch { /* not json */ }
        throw new Error(msg);
    }
    return res.blob();
};

/**
 * POST a FormData with upload progress (fetch cannot report it). onProgress gets 0–100.
 * Resolves with the JSON answer; rejects with an Error carrying status / payload / problems
 * like the API client does.
 */
export const uploadWithProgress = (url, formData, onProgress, method = 'POST') => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('jwtToken')}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
        let body = null;
        try { body = JSON.parse(xhr.responseText); } catch { /* not json */ }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
        const msg = xhr.status === 413 && !(body && body.message)
            ? 'The file is too large for the server to accept.' : (body && body.message) || `Upload failed (${xhr.status})`;
        const err = new Error(msg);
        err.status = xhr.status; err.payload = body; err.problems = body && body.problems;
        return reject(err);
    };
    xhr.onerror = () => reject(new Error('The upload was interrupted. Check the connection and try again.'));
    xhr.send(formData);
});

export const ProgressBar = ({ pct, label }) => (
    <div className="my-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-2 bg-slate-200 rounded overflow-hidden"><div className="h-2 bg-indigo-600 transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="text-xs text-slate-600 mt-1">{label || 'Uploading'} — {pct}%</div>
    </div>
);

export const fmtBytes = (n) => {
    if (n == null) return '—';
    const v = Number(n);
    if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)} GB`;
    if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB`;
    if (v >= 1024) return `${Math.round(v / 1024)} KB`;
    return `${v} B`;
};

/** The audiences a course can name (lib/training ROLES + all + supervisor). */
export const AUDIENCES = ['all', 'agent', 'managing_broker', 'office_staff', 'pm_staff', 'supervisor'];
export const SOURCE_TEXT = { requirement: 'Automatic — required rule', weekly: 'Automatic — Training of the week', monthly: 'Automatic — Module of the month',
    program: 'Automatic — 90-day programme', manual: 'Assigned by hand', self: 'Taken by the person', live_session: 'Live session attendance' };
